/**
 * Asking WHICH source a file comes from (#1069 Story 4.1, AC 1, code review
 * 2026-09-13).
 *
 * ⚠️ THIS IS THE HALF OF AC 1 THAT WAS MISSING. `messageFilePicker.native.ts`
 * has always implemented all three sources and `nativePickerSources` has always
 * named them, but the panel hardcoded `'files'`, so on iOS and Android the
 * paperclip opened the document browser and the photo library and the camera
 * were unreachable code. "A photo of the nameplate" — the case the story is
 * written around — meant leaving the app.
 *
 * ⚠️ NO SIBLINGS. This file must not gain `.web`/`.native` copies: Metro and
 * Jest's react-native preset resolve `./pickSourceChooser` to a `.native`
 * sibling the moment one exists, which is how Story 4.1's Task 1 lost a green
 * suite (T1-D3). The platform difference is already expressed by
 * `pickerSources`, which IS platform-resolved, so this file only has to do
 * arithmetic on the list it is handed.
 *
 * The dialog mirrors `src/components/forms/FileUpload.tsx:663-720` exactly —
 * `ActionSheetIOS` on iOS, `Alert.alert` with buttons on Android, Cancel last.
 * That is the product's established "where should this come from?" UI and this
 * is not the place to invent a second one.
 */

import { ActionSheetIOS, Alert, Platform } from 'react-native';

import type { PickSource } from './messageFilePicker';

/** House copy, from `FileUpload.tsx`'s sheet. */
export const SOURCE_LABELS: Record<PickSource, string> = {
  photos: 'Choose from Photos',
  files: 'Choose from Files',
  camera: 'Take Photo',
};

export const SHEET_TITLE = 'Attach a file';
export const SHEET_MESSAGE = 'Choose where the file comes from';

/**
 * The order the options are OFFERED in, which is the house order rather than
 * whatever order the platform module happens to export. Photos first: it is the
 * common case on a phone and the one the story is about.
 */
const OFFER_ORDER: readonly PickSource[] = ['photos', 'files', 'camera'];

/**
 * Ask, and resolve with the chosen source — or `null` if the person backed out.
 *
 * ⚠️ ONE SOURCE MEANS NO DIALOG. Web has exactly one (`webPickerSources`), and
 * a sheet offering a single option is a tax, not a choice. This is also why the
 * function is safe to call unconditionally from the panel.
 */
export const chooseFileSource = (
  sources: readonly PickSource[],
): Promise<PickSource | null> => {
  const offered = OFFER_ORDER.filter(source => sources.includes(source));

  if (offered.length === 0) return Promise.resolve(null);
  if (offered.length === 1) return Promise.resolve(offered[0]);

  return new Promise(resolve => {
    const labels = offered.map(source => SOURCE_LABELS[source]);

    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: [...labels, 'Cancel'],
          cancelButtonIndex: labels.length,
          title: SHEET_TITLE,
          message: SHEET_MESSAGE,
        },
        index => resolve(offered[index] ?? null),
      );
      return;
    }

    /**
     * ⚠️ NO CANCEL BUTTON ON ANDROID, AND IT IS NOT AN OVERSIGHT (code review
     * 2026-09-13, owner decision).
     *
     * React Native's `Alert` keeps only the LAST THREE buttons —
     * `Libraries/Alert/Alert.js:99`, `buttons.slice(0, 3)`, "At most three
     * buttons (neutral, negative, positive). Ignore rest." With three sources
     * plus a Cancel, the slice silently DROPPED Cancel, so the sheet shipped
     * without the control this code claimed to add. (`FileUpload.tsx:720` has
     * the same latent bug for the same reason; the decision was to match the
     * house rather than diverge from it here.)
     *
     * So the three sources ARE the three buttons, and backing out is the back
     * button or a tap outside, which `cancelable: true` enables and `onDismiss`
     * reports. On an Android version where `onDismiss` does not fire, this
     * promise never settles — acceptable ONLY because nothing awaits it behind
     * a busy flag: `pickFiles` simply never proceeds, exactly as if nothing had
     * been chosen. Do not add such a flag without revisiting this.
     *
     * ⚠️ THE ORDER IS A CONSEQUENCE OF THE SLICE, NOT A CHOICE. `Alert` pops
     * from the end, so positive = Camera, negative = Files, neutral = Photos,
     * and Android renders neutral | negative | positive left to right — which
     * is exactly `OFFER_ORDER`. A fourth button would break the order as well
     * as being discarded. Keep this list at three.
     */
    Alert.alert(
      SHEET_TITLE,
      SHEET_MESSAGE,
      offered.map((source, i) => ({
        text: labels[i],
        onPress: () => resolve(source),
      })),
      { cancelable: true, onDismiss: () => resolve(null) },
    );
  });
};
